import React, { useState, useEffect } from "react";
import {
  Text,
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
} from "react-native";

import moment from "moment";
import { range } from "lodash";

import Icons from "../../common/icons";

import Utils from "../../../../helpers/utils";

import AppTheme from "../../AppTheme";
import RoutinesIconWhite from "../../common/icons/RoutinesIconWhite";
import Header from "../../common/header/index";
import EditWhitePencil from "../../common/icons/integrations/EditWhitePencil";

const DATE_FORMAT = "YYYY-MM-DD";
const FUTURE_DAYS = 30;

export default function Week({
  journalContext,
  selectedDate,
  hideWeekSelector = false,
  toggleCalendar = () => {},
  updateSelectedDate = () => {},
}) {
  const {
    TIMESLOTS,
    controller,
    showTutorials,
    goBackOneStep,
    isLoading,
    isAutoTrackMode,
    showEmptyState = false,
    showRoutinesDialog = () => {},
    isBulkEdit = false,
    toggleBulkEditMode = () => {},
  } = journalContext;
  const { week_start } = controller.state?.settings || {};
  const weekStart = week_start == "Sunday" ? "week" : "isoWeek";

  const [weekDays, setWeekDays] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(
    Utils.nowUserTz(controller).format("MMMM"),
  );

  useEffect(() => {
    if (!selectedDate) return;

    setCurrentMonth(moment(selectedDate, DATE_FORMAT).format("MMMM"));
    initWeek(selectedDate);
  }, [selectedDate, TIMESLOTS]);

  const initWeek = (userSelectedDate) => {
    const now = Utils.nowUserTz(controller);
    const today = now.format(DATE_FORMAT);
    const startWeek = moment(userSelectedDate).startOf(weekStart);

    let days = [];
    range(0, 7).forEach((dayIndex) => {
      let date = startWeek.add(dayIndex == 0 ? 0 : 1, "days");
      const fullDate = date.format(DATE_FORMAT);
      const daysDiff = date.diff(today, "days");

      days.push({
        fullDate: fullDate,
        isSelected: fullDate === userSelectedDate,
        isToday: fullDate === today,
        isDisabled: daysDiff >= FUTURE_DAYS,
        isFutureDay: daysDiff >= 1,
        hasUnconfirmed:
          daysDiff <= 0 &&
          TIMESLOTS[fullDate] &&
          TIMESLOTS[fullDate].hasUnconfirmed,
        name: date.format("dd").charAt(0),
        number: date.format("D"),
      });
    });

    setWeekDays(days);
  };

  const changeDate = (day) => {
    if (showTutorials || day.isDisabled) return;
    updateSelectedDate(day.fullDate);
  };

  const changeWeek = (offset) => {
    if (showTutorials) return;

    let newDate = moment(selectedDate).add(offset, "week");

    if (offset < 0) {
      newDate = newDate.endOf(weekStart);
    } else {
      newDate = newDate.startOf(weekStart);
    }

    // Don't go past current week
    //if (newDate.week() > Utils.nowUserTz(controller).week()) return;

    updateSelectedDate(newDate.format(DATE_FORMAT));
  };

  return (
    <Header
      controller={controller}
      title={currentMonth}
      isLoading={isLoading}
      style={styles.header}
      // if EditMode active pressing More Menu will deactive it
      onMoreIconPressed={isBulkEdit ? () => toggleBulkEditMode(false) : null}
      goBack={showTutorials ? goBackOneStep : null}
      iconTag={
        showEmptyState ? null : (
          <EditWhitePencil
            fillColor={isBulkEdit ? "#F6EC91" : "#FFF"}
            onPress={() => toggleBulkEditMode(!isBulkEdit)}
          />
        )
      }
      iconTag2={
        isAutoTrackMode ? null : (
          <RoutinesIconWhite
            onPress={showRoutinesDialog}
            style={isBulkEdit ? styles.isEditModeDisabled : {}}
          />
        )
      }
      iconTag3={
        <Icons.Calendar
          onPress={toggleCalendar}
          fillColor={AppTheme.iconBlack}
          style={isBulkEdit ? styles.isEditModeDisabled : {}}
        />
      }
    >
      {!hideWeekSelector ? (
        <View style={styles.week}>
          <Icons.ArrowLeft
            fillColor={AppTheme.iconBlack}
            style={styles.arrowLeft}
            onPress={() => changeWeek(-1)}
          />
          <Icons.ArrowRight
            fillColor={AppTheme.iconBlack}
            style={styles.arrowRight}
            onPress={() => changeWeek(1)}
          />

          {weekDays.map((day, index) => (
            <TouchableOpacity
              key={index}
              onPress={() => changeDate(day)}
              style={[
                styles.weekDay,
                day.isSelected
                  ? styles.selectedWeekDay
                  : isBulkEdit
                  ? styles.isEditModeDisabled
                  : day.isFutureDay
                  ? styles.isDisabled
                  : {},
              ]}
            >
              <Text
                style={[
                  styles.dayName,
                  day.isSelected && styles.selectedDayName,
                ]}
              >
                {day.name}
              </Text>
              <Text
                style={[
                  styles.dayNumber,
                  day.isSelected && styles.selectedDayNumber,
                ]}
              >
                {day.number}
              </Text>
              <View
                style={[
                  styles.dot,
                  day.isToday
                    ? styles.today
                    : day.hasUnconfirmed
                    ? styles.hasUnconfirmed
                    : {},
                  day.isSelected && styles.dotSelected,
                ]}
              />
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
    </Header>
  );
}

const styles = StyleSheet.create({
  header: {
    ...Platform.select({
      web: {
        minHeight: 27,
        paddingTop: 34,
      },
      ios: {
        minHeight: 127,
        paddingTop: 52,
      },
      android: {
        paddingTop: 16,
      },
    }),
  },
  week: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-evenly",
    width: "100%",
    paddingBottom: 5,
    paddingHorizontal: 45,
    height: 75,
    marginTop: 5,
  },
  weekDay: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    height: 70,
    width: 38,
    paddingVertical: 10,
    paddingHorizontal: 8,
    paddingBottom: 12,
  },
  dayName: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.week.dayText,
  },
  dayNumber: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 1,
    color: AppTheme.week.dayText,
    width: "100%",
    textAlign: "center",
    marginTop: 9,
  },
  selectedWeekDay: {
    backgroundColor: "#F3F2F1",
    borderWidth: 1,
    borderColor: AppTheme.DEFAULT_COLOR,
    borderRadius: 20,
    height: 100,
  },
  selectedDayName: {
    color: AppTheme.DEFAULT_COLOR,
    opacity: 1,
  },
  selectedDayContainer: {
    borderRadius: 28,
    backgroundColor: "#F3F2F1",
    width: 28,
    height: 28,
    marginTop: 1,
  },
  selectedDayNumber: {
    paddingTop: 1,
    color: AppTheme.DEFAULT_COLOR,
    height: 28,
    lineHeight: 28,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 0,
  },
  isEditModeDisabled: {
    opacity: 0.4,
  },
  isDisabled: {
    opacity: 0.25,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    position: "absolute",
    bottom: 0,
    left: 15,
  },
  dotSelected: {
    bottom: 30,
  },
  hasUnconfirmed: {
    backgroundColor: "#F6DFA7",
  },
  today: {
    backgroundColor: "#925BE8",
  },
  arrowRight: {
    height: "100%",
    position: "absolute",
    top: 0,
    right: 11,
  },
  arrowLeft: {
    height: "100%",
    position: "absolute",
    left: 5,
    top: 0,
  },
  rightContainer: {
    position: "absolute",
    right: 6,
    top: 0,
    height: "100%",
    zIndex: 2,
    minWidth: 40,
  },
  loading: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 40,
    justifyContent: "center",
  },
});
